import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeatureEntitlements } from "@frontegg/react";
import Button from "@mui/material/Button";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CreditCardAddIcon,
  Invoice03Icon,
  UserAdd01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useAppEntitlements } from "hooks/useAppEntitlements";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { Inline } from "components/layout/reusableLayouts";
import AddNewUserDrawer from "components/admin/users/AddNewUserDrawer";
import CardFormDrawer from "components/common/cards/CardFormDrawer";

/**
 * The dashboard's create shortcuts, rendered in the navbar rather than on the
 * page. Each one is gated the same way its own page gates the equivalent
 * action, so a role that cannot finish the flow is never offered it.
 *
 * The drawers are the same components the Users and Cards pages open; nothing
 * on this page lists what they create, so their refresh callbacks are no-ops.
 */
const DashboardQuickActions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userDrawer = useDrawerRecord<true>();
  const cardDrawer = useDrawerRecord<true>();

  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAccountant = useCheckUserRole(Role.Accountant, { exact: true });
  const isAuditorOrAccountant = useMemo(
    () => isAuditor || isAccountant,
    [isAuditor, isAccountant],
  );

  const { isEntitled: featureInvoices } =
    useFeatureEntitlements("feature_invoices");
  const { spend } = useAppEntitlements();

  // Issuing needs the Spend app's card issuer program; a BYOC-only company
  // links its own cards instead, so the shortcut would dead-end.
  const showIssueCard = spend && !isAuditorOrAccountant;
  const showCreateInvoice = featureInvoices && !isAuditor;

  if (!isAdminOrHigher && !showIssueCard && !showCreateInvoice) return null;

  return (
    <>
      <Inline gap="var(--space-3)" wrap={false}>
        {isAdminOrHigher && (
          <Button
            variant="default-outlined"
            startIcon={<HugeiconsIcon icon={UserAdd01Icon} size={18} />}
            onClick={() => userDrawer.show(true)}
          >
            {t("Add User")}
          </Button>
        )}

        {showIssueCard && (
          <Button
            variant="default-outlined"
            startIcon={<HugeiconsIcon icon={CreditCardAddIcon} size={18} />}
            onClick={() => cardDrawer.show(true)}
          >
            {t("Issue Card")}
          </Button>
        )}

        {showCreateInvoice && (
          <Button
            variant="default-outlined"
            startIcon={<HugeiconsIcon icon={Invoice03Icon} size={18} />}
            onClick={() =>
              navigate("/admin/invoices/send-an-invoice", {
                state: { activeTab: 2 },
              })
            }
          >
            {t("Create Invoice")}
          </Button>
        )}
      </Inline>

      {userDrawer.record && (
        <AddNewUserDrawer
          open={userDrawer.open}
          handleCloseDrawer={userDrawer.close}
          onAfterClose={userDrawer.afterClose}
          handleCreatedUser={() => {}}
        />
      )}

      {cardDrawer.record && (
        <CardFormDrawer
          open={cardDrawer.open}
          onClose={cardDrawer.close}
          onAfterClose={cardDrawer.afterClose}
        />
      )}
    </>
  );
};

export default DashboardQuickActions;
